import type { schema } from 'database';

export const THEME_SETTING_NOTICE =
  'Some settings do not apply to the current theme.';

export type ThemeControlId =
  | 'all.font'
  | 'all.colors'
  | 'all.profile'
  | 'all.borderRadius'
  | 'all.headerActions'
  | 'all.search'
  | 'all.footer'
  | 'home.hero'
  | 'home.featuredPost'
  | 'home.tagTabs'
  | 'home.sortMenu'
  | 'home.defaultSort'
  | 'home.newsletter'
  | 'post.breadcrumb'
  | 'post.readTime'
  | 'post.excerpt'
  | 'post.coverImage'
  | 'post.sharing'
  | 'post.aside'
  | 'post.toc'
  | 'post.comments'
  | 'post.likes'
  | 'post.morePosts'
  | 'post.newsletter';

export type ThemeControlPolicy =
  | { mode: 'configurable' }
  | { mode: 'fixed'; value: unknown }
  | { mode: 'unsupported' };

export type ThemeDefinition = {
  id: schema.ThemeType;
  label: string;
  stylesheet: string;
  colorScheme: 'light' | 'dark';
  controls: Partial<Record<ThemeControlId, ThemeControlPolicy>>;
};

const configurable = { mode: 'configurable' } as const;

export const THEME_REGISTRY: Record<schema.ThemeType, ThemeDefinition> = {
  Modern: {
    id: 'Modern',
    label: 'Modern',
    stylesheet: '/.quotion/themes/modern.css',
    colorScheme: 'light',
    controls: {},
  },
  Dracula: {
    id: 'Dracula',
    label: 'Dracula',
    stylesheet: '/.quotion/themes/dracula.css',
    colorScheme: 'dark',
    controls: {},
  },
  Minimal: {
    id: 'Minimal',
    label: 'Minimal',
    stylesheet: '/.quotion/themes/minimal.css',
    colorScheme: 'light',
    controls: {
      'all.colors': { mode: 'unsupported' },
      'all.profile': { mode: 'unsupported' },
      'all.headerActions': { mode: 'unsupported' },
      'all.search': configurable,
      'all.footer': { mode: 'unsupported' },
      'home.hero': { mode: 'unsupported' },
      'post.breadcrumb': { mode: 'fixed', value: false },
      'post.readTime': { mode: 'fixed', value: false },
      'post.excerpt': { mode: 'fixed', value: false },
      'post.coverImage': { mode: 'fixed', value: false },
      'post.sharing': { mode: 'unsupported' },
      'post.aside': { mode: 'unsupported' },
      'post.toc': { mode: 'unsupported' },
      'post.likes': { mode: 'unsupported' },
      'post.morePosts': { mode: 'fixed', value: false },
    },
  },
};

export function resolveTheme(
  theme: schema.ThemeType | string | null | undefined,
): ThemeDefinition {
  if (theme && Object.hasOwn(THEME_REGISTRY, theme)) {
    return THEME_REGISTRY[theme as schema.ThemeType];
  }
  return THEME_REGISTRY.Modern;
}

export function getThemeControlPolicy(
  theme: schema.ThemeType | string | null | undefined,
  control: ThemeControlId,
): ThemeControlPolicy {
  return resolveTheme(theme).controls[control] ?? configurable;
}

export function isThemeControlConfigurable(
  theme: schema.ThemeType | string | null | undefined,
  control: ThemeControlId,
): boolean {
  return getThemeControlPolicy(theme, control).mode === 'configurable';
}

export function resolveThemeControlValue<T>(
  theme: schema.ThemeType | string | null | undefined,
  control: ThemeControlId,
  storedValue: T,
): T | undefined {
  const policy = getThemeControlPolicy(theme, control);
  if (policy.mode === 'unsupported') {
    return undefined;
  }
  if (policy.mode === 'fixed') {
    return policy.value as T;
  }
  return storedValue;
}

export function hasInactiveThemeControls(
  theme: schema.ThemeType | string | null | undefined,
): boolean {
  return Object.values(resolveTheme(theme).controls).some(
    (policy) => policy && policy.mode !== 'configurable',
  );
}

export function usesFolderHomeLayout(
  theme: schema.ThemeType | string | null | undefined,
): boolean {
  return resolveTheme(theme).id === 'Minimal';
}

export type PostThemeSettings = {
  showBreadcrumb: boolean;
  showReadTime: boolean;
  showExcerpt: boolean;
  extractCoverImage: boolean;
  showToc: boolean;
  showComment: boolean;
  showLike: boolean;
  showMoreFrom: boolean;
};

export function resolvePostThemeSettings(
  theme: schema.ThemeType | string | null | undefined,
  stored: PostThemeSettings,
): PostThemeSettings {
  return {
    showBreadcrumb:
      resolveThemeControlValue(
        theme,
        'post.breadcrumb',
        stored.showBreadcrumb,
      ) ?? false,
    showReadTime:
      resolveThemeControlValue(theme, 'post.readTime', stored.showReadTime) ??
      false,
    showExcerpt:
      resolveThemeControlValue(theme, 'post.excerpt', stored.showExcerpt) ??
      false,
    extractCoverImage:
      resolveThemeControlValue(
        theme,
        'post.coverImage',
        stored.extractCoverImage,
      ) ?? false,
    showToc:
      resolveThemeControlValue(theme, 'post.toc', stored.showToc) ?? false,
    showComment:
      resolveThemeControlValue(theme, 'post.comments', stored.showComment) ??
      false,
    showLike:
      resolveThemeControlValue(theme, 'post.likes', stored.showLike) ?? false,
    showMoreFrom:
      resolveThemeControlValue(theme, 'post.morePosts', stored.showMoreFrom) ??
      false,
  };
}
